import cn from 'classnames';
import styles from './LeaveReviewForm.module.scss';
import { FETCH_METHODS, ProductDetailedType } from '@/types';
import { ModalContentWrapper } from '@/atoms/ModalContentWrapper';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { RatingChooser } from '@/atoms/RatingChooser';
import { Input } from '@/atoms/Input';
import { MESSAGE_REGEX } from '@/constants';
import { Toggle } from '@/components/Toggle';
import { Button } from '@/atoms/Button';
import { useDispatch } from 'react-redux';
import { setShowLeaveReviewModal } from '@/store';
import { useState } from 'react';
import { InputFile } from '@/atoms/InputFile';
import { ProductReduced } from '@/components/ProductReduced';
import { SuccessActionContent } from '@/atoms/SuccessActionContent';
import { fetchFormData } from '@/services';
import toast from 'react-hot-toast';

type LeaveReviewFormProps = {
  className?: string;
  product: ProductDetailedType;
};

type ReviewFormInputs = {
  rating: number;
  advantages: string;
  disadvantages: string;
  comment: string;
  photos: File[];
  anonymous: boolean;
};

export function LeaveReviewForm({ className, product }: LeaveReviewFormProps) {
  const wrapperClassName = cn(styles.wrapper, className);
  const dispatch = useDispatch();
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClickRating = (value: number) => {
    methods.setValue('rating', value);
  };

  const handleClose = () => {
    dispatch(setShowLeaveReviewModal(false));
  };

  const methods = useForm<ReviewFormInputs>();

  const onSubmit: SubmitHandler<ReviewFormInputs> = async (data) => {
    setIsLoading(true);

    const response = await fetchFormData({
      url: '/form/review',
      method: FETCH_METHODS.POST,
      body: {
        isAnonymous: data.anonymous,
        rating: data.rating,
        advantages: data.advantages,
        flaws: data.disadvantages,
        description: data.comment,
        photo: data.photos,
        productId: product.id,
      },
    });

    if (response.success) {
      setSuccess(true);
    } else {
      toast.error('Ошибка при отправке отзыва');
    }

    setIsLoading(false);
  };

  const fields = useWatch({ control: methods.control });

  const handleSubmit = methods.handleSubmit(onSubmit);

  const isDisabled =
    !fields.rating ||
    !fields.advantages ||
    !fields.disadvantages ||
    !fields.comment ||
    isLoading;

  if (success) {
    return (
      <SuccessActionContent
        title="Отзыв отправлен"
        buttonText="Вернуться к покупкам"
        onClick={handleClose}
      />
    );
  }
  return (
    <div className={wrapperClassName}>
      <ModalContentWrapper title="Оставить отзыв" onClose={handleClose}>
        <div className={styles.content}>
          <ProductReduced product={product} />
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit} className={styles.form}>
              <RatingChooser
                value={fields.rating ?? 0}
                onChange={handleClickRating}
              />
              <div className={styles.field}>
                <span className={styles.fieldTitle}>Достоинства</span>
                <Input
                  name="advantages"
                  placeholder="Есть"
                  pattern={MESSAGE_REGEX}
                  required
                />
              </div>
              <div className={styles.field}>
                <span className={styles.fieldTitle}>Недостатки</span>
                <Input
                  name="disadvantages"
                  placeholder="Нету"
                  pattern={MESSAGE_REGEX}
                  required
                />
              </div>
              <div className={styles.field}>
                <span className={styles.fieldTitle}>Комментарий</span>
                <Input
                  name="comment"
                  placeholder="Всё хорошо"
                  pattern={MESSAGE_REGEX}
                  required
                />
              </div>
              <div className={styles.field}>
                <span className={styles.fieldTitle}>Добавьте фото</span>
                <InputFile name="photos" label="Фото" multiple max={5} />
              </div>
              <Toggle
                label="Анонимно"
                checked={fields.anonymous ?? false}
                onChange={() =>
                  methods.setValue('anonymous', !fields.anonymous)
                }
              />
              <Button
                text="Отправить отзыв"
                onClick={() => {}}
                type="submit"
                variant="primary"
                disabled={isDisabled}
              />
            </form>
          </FormProvider>
        </div>
      </ModalContentWrapper>
    </div>
  );
}
